import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Server, Terminal as TerminalIcon, Search, ChevronRight } from 'lucide-react';
import { useTerminalConfigStore } from '../../store/terminalConfigStore';
import AgentMenuRow from './AgentMenuRow';
import ProfileInstallHint from './ProfileInstallHint';
import OsScroll from '../ui/OsScroll';
import { buildLaunchRows, installState } from './launchListRows';
import { useLaunchListNav } from './useLaunchListNav';
import './TerminalMenu.css';

/**
 * The searchable launch list shared by the "+" menu and the "Split with…"
 * picker: optional `leadRows` (e.g. "Same as this pane"), AGENTS (profiles
 * with install state; a missing CLI opens its install hint instead of
 * launching), SHELLS (`shells`) and, when `showSsh`, "SSH connection…".
 * Keyboard: type to filter, ↑/↓ to move, Enter to pick, Esc → `onClose`.
 * Renders the whole panel (`className`/`style`), with `header` above the
 * search box and `footer` below the list.
 *
 * Callbacks get the triggering event so a caller can read modifiers:
 * `onAgent(profile, event, where)` (`where` is set by a row's own buttons:
 * 'row' / 'col' / 'folder' when `agentSplitActions`, only 'folder' when
 * `agentFolderAction`), `onShell(shell, event)`,
 * `onSsh()`. A lead row is `{ key, label, icon, hint?, onPick(event) }`.
 */
export default function TerminalLaunchList({
  shells = [],
  leadRows = [],
  showSsh = false,
  agentSplitActions = false,
  agentFolderAction = false,
  agentTip,
  onAgent,
  onShell,
  onSsh,
  onClose,
  header = null,
  footer = null,
  className = '',
  style,
  panelRef,
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [hintFor, setHintFor] = useState(null);
  const profiles = useTerminalConfigStore((s) => s.profiles);
  const detection = useTerminalConfigStore((s) => s.detection);
  const detecting = useTerminalConfigStore((s) => s.detecting);

  useEffect(() => {
    const st = useTerminalConfigStore.getState();
    st.load().then(() => st.detect());
  }, []);

  const sshLabel = t('terminalExt.openSsh', 'SSH connection…');
  const { leads, agents, shellRows, sshVisible, rows } = buildLaunchRows({ query, leadRows, profiles, shells, showSsh, sshLabel });

  const pickAgent = (p, e, where) => {
    if (installState(detection, p) === 'no') {
      setHintFor((cur) => (cur === p.id ? null : p.id));
      return;
    }
    onAgent(p, e, where);
  };

  const activate = (row, e) => {
    if (!row) return;
    if (row.type === 'lead') row.lead.onPick(e);
    else if (row.type === 'agent') pickAgent(row.profile, e);
    else if (row.type === 'shell') onShell(row.shell, e);
    else onSsh?.();
  };

  const { listRef, onKeyDown, rowProps, resetActive } = useLaunchListNav(rows, { onPick: activate, onClose });
  const find = (key) => rows.find((r) => r.key === key);

  return (
    <div className={className} style={style} ref={panelRef} onKeyDown={onKeyDown}>
      {header}
      <div className="term-menu-search">
        <Search size={13} aria-hidden />
        <input
          autoFocus
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            resetActive();
          }}
          placeholder={t('terminalExt.menuFilter', 'Open an agent or a shell…')}
          aria-label={t('terminalExt.menuFilter', 'Open an agent or a shell…')}
          autoComplete="off"
          spellCheck={false}
        />
      </div>
      <OsScroll className="term-menu-list">
        <div className="term-menu-list-inner" ref={listRef} role="menu">
          {leads.map((lead) => {
            const row = find(`l:${lead.key}`);
            return (
              <div key={lead.key} {...rowProps()} onClick={(e) => activate(row, e)}>
                {lead.icon}
                <span className="term-menu-row-name">{lead.label}</span>
                {lead.hint && <span className="term-menu-row-status term-menu-row-hint">{lead.hint}</span>}
              </div>
            );
          })}
          {agents.length > 0 && <div className="term-menu-section">{t('terminalExt.agents', 'Agents')}</div>}
          {agents.map((p) => {
            const row = find(`a:${p.id}`);
            return (
              <React.Fragment key={p.id}>
                <AgentMenuRow
                  profile={p}
                  state={installState(detection, p)}
                  rowProps={rowProps()}
                  hintOpen={hintFor === p.id}
                  tip={agentTip}
                  onOpen={agentSplitActions || agentFolderAction ? (where) => pickAgent(p, null, where) : null}
                  showSplits={agentSplitActions}
                  onClickRow={(e) => activate(row, e)}
                />
                {hintFor === p.id && (
                  <ProfileInstallHint
                    profile={p}
                    rechecking={detecting}
                    onRecheck={() => useTerminalConfigStore.getState().detect(true)}
                  />
                )}
              </React.Fragment>
            );
          })}
          {(shellRows.length > 0 || sshVisible) && <div className="term-menu-section">{t('terminalExt.shells', 'Shells')}</div>}
          {shellRows.map((shell) => {
            const row = find(`s:${shell.key}`);
            return (
              <div key={shell.key} {...rowProps()} onClick={(e) => activate(row, e)}>
                <TerminalIcon size={14} className="term-menu-row-icon" aria-hidden />
                <span className="term-menu-row-name">{shell.label}</span>
              </div>
            );
          })}
          {sshVisible && (
            <div {...rowProps()} onClick={(e) => activate({ type: 'ssh' }, e)}>
              <Server size={14} className="text-emerald-600 flex-shrink-0" aria-hidden />
              <span className="term-menu-row-name">{sshLabel}</span>
              <ChevronRight size={13} className="term-menu-row-chev" aria-hidden />
            </div>
          )}
          {rows.length === 0 && <div className="term-menu-empty">{t('terminalExt.menuNoMatch', 'Nothing matches.')}</div>}
        </div>
      </OsScroll>
      {footer}
    </div>
  );
}
